/**
 * Export d'un <svg> du DOM en image PNG : copie dans le presse-papier si
 * disponible (ClipboardItem), sinon télécharge un PNG en fallback.
 *
 * @param {SVGSVGElement} svgEl  l'élément <svg> à exporter
 * @param {{ background?: string, filename?: string, scale?: number }} opts
 * @returns {Promise<'clipboard'|'download'>}
 */
export async function copySvgAsImage(svgEl, opts = {}) {
  if (!svgEl) throw new Error('svgEl manquant');
  const { background = '#ffffff', filename = 'rapport.png', scale = 2 } = opts;

  // Dimensions : viewBox ou bounding box.
  const rect = svgEl.getBoundingClientRect();
  const width = Math.max(1, Math.round(rect.width || svgEl.clientWidth || 600));
  const height = Math.max(1, Math.round(rect.height || svgEl.clientHeight || 300));

  // Clone + namespace explicite pour la sérialisation.
  const clone = svgEl.cloneNode(true);
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));

  const xml = new XMLSerializer().serializeToString(clone);
  const svgBlob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  try {
    const img = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);
    if (background) {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, width, height);
    }
    ctx.drawImage(img, 0, 0, width, height);

    const pngBlob = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/png'),
    );
    if (!pngBlob) throw new Error('toBlob a échoué');

    // Tentative presse-papier.
    if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      typeof window !== 'undefined' &&
      typeof window.ClipboardItem !== 'undefined'
    ) {
      try {
        await navigator.clipboard.write([
          new window.ClipboardItem({ 'image/png': pngBlob }),
        ]);
        return 'clipboard';
      } catch {
        // Permission refusée / contexte non sécurisé → fallback download.
      }
    }
    downloadBlob(pngBlob, filename);
    return 'download';
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

function downloadBlob(blob, filename) {
  const a = document.createElement('a');
  const href = URL.createObjectURL(blob);
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

export default copySvgAsImage;
